import classNames from "classnames";
import { motion } from "motion/react";
import { Suspense } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "reactstrap";
import { useProductWithCockpitId } from "../../hooks/ProductHooks";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { transformHtml } from "../../utils/CmsUtils";
import { unwrapAsset } from "../../utils/CockpitUtils";
import { useProductUrl } from "../../utils/ProductUtils";
import ImageOrVideo from "../ImageOrVideo";
import GuideAnimation from "./GuideAnimation";
import GuideProductInfo from "./GuideProductInfo";
import "./style.scss";

const GuideStepDetail = ({
  index = 0,
  component = "guide_step",
  image,
  animation,
  product_link,
  label,
  heading,
  description,
  sectionStepCount = 0,
  fullWidth = false,
  ...rest
}) => {
  const t = useBjelinTranslations();
  const transition = {
    type: "spring",
    damping: 100,
    stiffness: 2000,
    mass: 0.1
  };
  const product = useProductWithCockpitId(product_link?._id);
  const url = useProductUrl(product);

  return (
    <motion.div
      {...rest}
      className="guide-step guide-step-detail carousel-slide"
      id={"guide-step-detail-" + index}
      initial={{ scale: 0.5 }}
      animate={{
        opacity: 1,
        scale: 1,
        x: 0
      }}
      exit={{ scale: 0.5, opacity: 0 }}
      transition={transition}
      style={{
        maxHeight: `calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom) - var(--menu-height) - var(--padding-top))`
      }}
    >
      <div
        className={classNames("guide-step-inner", {
          "has-landscape-layout": !heading
        })}
        style={{
          maxHeight: `calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom) - var(--menu-height) - var(--padding-top))`
        }}
      >
        <Suspense
          fallback={
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "70vh"
              }}
            >
              <Spinner />
            </div>
          }
        >
          {heading ? (
            <>
              <h2 className="guide-section-heading">{heading}</h2>
              <p className="guide-section-description">
                {sectionStepCount} {t("guide.steps")}
              </p>
            </>
          ) : (
            <>
              {product ? (
                <motion.div
                  className="guide-image"
                  style={{
                    backgroundColor: "#ddd"
                  }}
                >
                  <Link to={url} target="_blank" className={"image-container"}>
                    <img
                      src={product.images?.[0].styles.preview}
                      alt={product.name}
                      style={{
                        display: "block",
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "100%",
                        width: "100%",
                        objectFit: "cover"
                      }}
                    />
                  </Link>
                </motion.div>
              ) : (
                image && (
                  <motion.div
                    className="guide-image"
                    transition={transition}
                    style={
                      image?.colors?.[0] && {
                        backgroundColor: image.colors[0]
                      }
                    }
                  >
                    {animation?._id ? (
                      <GuideAnimation _id={animation._id} />
                    ) : (
                      image && (
                        <ImageOrVideo
                          image={unwrapAsset(image)}
                          style={{
                            width: "100%"
                          }}
                        />
                      )
                    )}
                  </motion.div>
                )
              )}
              <div className="guide-text">
                <motion.h3 transition={transition}>
                  {!fullWidth && !product && (
                    <span className="index-number">{index}</span>
                  )}
                  {label ? label : product ? product.name : ""}
                </motion.h3>
                <motion.div
                  className="guide-description"
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transition: { delay: 0.4, duration: 1.4 }
                  }}
                  style={{ overflowY: "auto" }}
                >
                  {transformHtml(description)}
                </motion.div>
                {product && <GuideProductInfo product={product} large />}
              </div>
            </>
          )}
        </Suspense>
      </div>
    </motion.div>
  );
};

export default GuideStepDetail;
